/**
 * UI state — Legend State observable store.
 * Mirrors the design prototype's initial state + nav methods.
 */
import { observable } from '@legendapp/state';
import { DEFAULT_PLATFORM, type PlatformSlug } from './data/sample';

/**
 * Marketing site lives on its own domain (scangap.com); the app never renders
 * landing pages. "Back to home" / logout link out to here.
 */
export const LANDING_URL: string = import.meta.env.VITE_LANDING_URL ?? 'https://scangap.com';

export type View = 'login' | 'explorer' | 'niche' | 'function' | 'app' | 'agent';
export type Tab = 'overview' | 'matrix' | 'opportunities' | 'apps';
export type SortKey = 'saturation' | 'momentum' | 'apps' | 'rating';

export interface Drawer {
  appId: string;
  funcId: string;
}

export interface UIState {
  view: View;
  /** Marketplace the Explorer is scoped to (top-bar switcher). */
  platformSlug: PlatformSlug;
  nicheId: string | null;
  funcId: string | null;
  appId: string | null;
  prevView: View | null;
  tab: Tab;
  search: string;
  sortKey: SortKey;
  colorByOpp: boolean;
  sortByOpp: boolean;
  defId: string | null;
  expandedOpp: string | null;
  drawer: Drawer | null;
}

export const state$ = observable<UIState>({
  view: 'login',
  platformSlug: DEFAULT_PLATFORM,
  nicheId: null,
  funcId: null,
  appId: null,
  prevView: null,
  tab: 'overview',
  search: '',
  sortKey: 'saturation',
  colorByOpp: true,
  sortByOpp: false,
  defId: null,
  expandedOpp: null,
  drawer: null,
});

function scrollTop(): void {
  const sc = document.scrollingElement;
  if (sc) sc.scrollTop = 0;
}

/** Apply a patch + reset scroll, mirroring the design's nav(). */
export function nav(patch: Partial<UIState>): void {
  state$.assign(patch);
  scrollTop();
}

export function openNiche(id: string): void {
  nav({ view: 'niche', nicheId: id, tab: 'overview', defId: null });
}

export function openFunc(id: string): void {
  nav({ view: 'function', funcId: id, prevView: state$.view.get() });
}

export function openApp(id: string): void {
  nav({ view: 'app', appId: id, prevView: state$.view.get() });
}

export function goExplorer(): void {
  nav({ view: 'explorer', nicheId: null, defId: null });
}
/** Switch marketplace and return to the (now filtered) Explorer. */
export function selectPlatform(slug: PlatformSlug): void {
  nav({ view: 'explorer', nicheId: null, defId: null, platformSlug: slug });
}
export function goAgent(): void {
  nav({ view: 'agent' });
}
export function goLogin(): void {
  nav({ view: 'login' });
}
export function doLogin(): void {
  nav({ view: 'explorer', nicheId: null, defId: null });
}
